import { z } from "zod";
import { COUNTRY_CODES } from "./constants";
import { isValidHours } from "./hours";

// Public restaurant profile (prototype Settings → Restaurant). Saved as a draft, published separately.
// Validation rules and messages are the prototype's `setErrors`.

const text = (max: number) => z.string().trim().max(max, "Textul este prea lung.").default("");
const httpsLink = text(300).refine(
  (v) => !v || /^https:\/\/\S+\.\S+$/.test(v),
  "Linkul trebuie să înceapă cu https://",
);

export const profileSchema = z.object({
  name: z.string().trim().min(1, "Adaugă numele restaurantului.").max(80, "Numele este prea lung."),
  descRo: text(200),
  descEn: text(200),
  address: text(120),
  city: text(120),
  country: z.enum(COUNTRY_CODES, "Alege țara."),
  phone: text(40).refine(
    (v) => !v || /^\+?[\d\s().-]{7,20}$/.test(v),
    "Folosește cifre, spații și +, de ex. +40 269 000 000.",
  ),
  email: text(254).refine((v) => !v || /^\S+@\S+\.\S+$/.test(v), "Adresa de e-mail nu pare validă."),
  website: httpsLink,
  facebook: httpsLink,
  instagram: httpsLink,
  google: httpsLink,
  tripadvisor: httpsLink,
  /** Opening hours, compact (shared/hours.ts); empty = not shown. */
  hours: text(400).refine(
    isValidHours,
    "Verifică programul: orele au forma 12:00, iar ora de închidere e diferită.",
  ),
  /** Guests' Wi-Fi (owner 2026-11-04), shown at the top of the public menu with a copy button. */
  wifiName: text(60),
  wifiPass: text(63),
  /** An announcement at the top of the public menu, between two dates (inclusive; empty = no limit). */
  announceRo: text(200),
  announceEn: text(200),
  announceFrom: text(10).refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), "Alege o dată."),
  announceTo: text(10).refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), "Alege o dată."),
});
export type PublicProfile = z.infer<typeof profileSchema>;

export const PROFILE_LABELS: Record<keyof PublicProfile, string> = {
  name: "Numele restaurantului",
  descRo: "Descriere scurtă · română",
  descEn: "Descriere scurtă · English",
  address: "Adresă",
  city: "Oraș",
  country: "Țară",
  phone: "Telefon",
  email: "E-mail de contact",
  website: "Website",
  facebook: "Facebook",
  instagram: "Instagram",
  google: "Pagina de recenzii Google",
  tripadvisor: "Pagina de recenzii Tripadvisor",
  hours: "Program",
  wifiName: "Numele rețelei Wi-Fi",
  wifiPass: "Parola Wi-Fi",
  announceRo: "Anunț · română",
  announceEn: "Anunț · English",
  announceFrom: "Anunțul apare de la",
  announceTo: "Anunțul apare până la",
};

export const PROFILE_FIELDS = Object.keys(PROFILE_LABELS) as (keyof PublicProfile)[];

export interface ProfileChange {
  field: keyof PublicProfile;
  label: string;
  from: string;
  to: string;
}

/** Field-level differences between the published profile and the draft. */
export function diffProfile(published: PublicProfile | null, draft: PublicProfile): ProfileChange[] {
  // A field missing from an older stored profile counts as empty (never as a change).
  return PROFILE_FIELDS.filter((f) => (published?.[f] ?? "") !== (draft[f] ?? "")).map((f) => ({
    field: f,
    label: PROFILE_LABELS[f],
    from: published?.[f] ?? "",
    to: draft[f] ?? "",
  }));
}

/** Canonical object with every field present, in a stable key order. */
export function normalizeProfile(
  p: Partial<PublicProfile> & Pick<PublicProfile, "name" | "country">,
): PublicProfile {
  return {
    name: p.name,
    descRo: p.descRo ?? "",
    descEn: p.descEn ?? "",
    address: p.address ?? "",
    city: p.city ?? "",
    country: p.country,
    phone: p.phone ?? "",
    email: p.email ?? "",
    website: p.website ?? "",
    facebook: p.facebook ?? "",
    instagram: p.instagram ?? "",
    google: p.google ?? "",
    tripadvisor: p.tripadvisor ?? "",
    hours: p.hours ?? "",
    wifiName: p.wifiName ?? "",
    wifiPass: p.wifiPass ?? "",
    announceRo: p.announceRo ?? "",
    announceEn: p.announceEn ?? "",
    announceFrom: p.announceFrom ?? "",
    announceTo: p.announceTo ?? "",
  };
}

/** Publish / discard confirm the profile revision the user saw. */
export const profileRevisionSchema = z.object({ revision: z.number("Revizia lipsește.").int().min(0) });
